import React, { useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { DeleteOutlined, SearchOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Select as SelectImpl } from '@components/shared/ui/Select';
import SelectNotFoundContentImpl from '@components/shared/ui/SelectNotFoundContent';
import { Alert, Card, Button, InputNumber, Row, Col, Input, DatePicker, Form, Switch, Select as AntdSelect, Tooltip, Typography, Tag, Space } from 'antd';
import type { FormInstance, FormListFieldData } from 'antd';
import InventorySelectorModal from '@components/shared/selectors/InventorySelectorModal';
import UnifiedItemSearch from '@components/shared/items/UnifiedItemSearch';

const { Text } = Typography;
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { toDayjsOrNull } from '@utils/dateTime';
import InventoryBatchTags from '@components/shared/items/InventoryBatchTags';
import { useTranslation } from 'react-i18next';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useMerchantData } from '@contexts/admin/MerchantDataContext';
import type { Bundle } from '@utils/orders/bundleExpansion';

// ── 型別 ──────────────────────────────────────────────────────────────

interface ProductLike {
  id: string;
  sku: string;
  name: string;
  validity_days?: number;
  manufacturing_date_required?: boolean;
  batch_management_required?: boolean;
  [key: string]: unknown;
}

interface ProductOption {
  value: string;
  label: string;
  sku?: string;
  name?: string;
  validity_days?: number;
  manufacturing_date_required?: boolean;
  batch_management_required?: boolean;
}

interface AttributeDefinition {
  id: string;
  name: string;
  label: string;
  data_type: string;
  required?: boolean;
  options?: unknown;
  [key: string]: unknown;
}

interface InventoryItem {
  id: string;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  custom_attributes?: Record<string, unknown>;
  inventory_type?: { id: string; name: string };
  available_quantity?: number;
  [key: string]: unknown;
}

interface InventoryTypeRow {
  id: string;
  code?: string;
  name: string;
  [key: string]: unknown;
}

interface FetchListParams {
  search?: string;
  pageSize?: number;
  sinceId?: unknown;
  limit?: number;
  since_id?: unknown;
}

// Param bag is forwarded as a query string — actual callers (e.g. useOperatorProductService)
// type it as `Record<string, unknown>`. Keep this prop signature wide so any such caller assigns.
interface SearchProductsService {
  fetchList: (params: Record<string, unknown>) => Promise<unknown>;
}

interface InventoryService {
  list: (params: Record<string, unknown>) => Promise<{ data?: InventoryItem[] }>;
}

interface SelectFetchDataParams {
  search?: string;
  pageSize?: number;
  sinceId?: unknown;
}

interface SelectFetchDataResult {
  options?: unknown[];
  hasMore?: boolean;
  sinceId?: unknown;
}

type FetchInventoryTypes =
  | ((params: SelectFetchDataParams) => Promise<SelectFetchDataResult>)
  | null;

type InventoryMode = 'input' | 'precise';
type Role = 'admin' | 'operator';

// `key` is React's reserved prop — callers always destructure it out before spreading restField,
// so it's never on the object at runtime. Reflect that in the type.
type RestField = Omit<FormListFieldData, 'name' | 'key'>;

export const mapProductToOption = (product: ProductLike): ProductOption => ({
  value: product.id,
  label: `${product.sku} - ${product.name}`,
  sku: product.sku,
  name: product.name,
  validity_days: product.validity_days,
  manufacturing_date_required: product.manufacturing_date_required,
  batch_management_required: product.batch_management_required,
});


// Wrapper that absorbs Form.Item's injected props (value, onChange, id)
// Used when rendering non-form-controlled content inside Form.Item
type FormFieldDisplayProps = {
  value?: unknown;
  onChange?: unknown;
  id?: string;
  onBlur?: unknown;
  onFocus?: unknown;
  children?: ReactNode;
  [key: string]: unknown;
};
const FormFieldDisplay = React.forwardRef<HTMLDivElement, FormFieldDisplayProps>(
  function FormFieldDisplay({ value, onChange, id, onBlur, onFocus, ...rest }, ref) {
    return <div ref={ref} {...(rest as React.HTMLAttributes<HTMLDivElement>)} />;
  }
);

interface ProductItemCardProps {
  restField: RestField;
  name: number;
  onRemove: (name: number) => void;
  searchProductsService?: SearchProductsService | null;
  inventoryTypes?: InventoryTypeRow[] | null;
  fetchInventoryTypes?: FetchInventoryTypes;
  attributeDefinitions?: AttributeDefinition[];
  renderExtraFields?: (name: number, restField: RestField, opts: { disabled: boolean }) => ReactNode;
  form: FormInstance;
  quantityLabel?: ReactNode;
  quantityReadOnly?: boolean;
  productReadOnly?: boolean;
  listName: string;
  quantityFieldName?: string;
  role?: Role;
  alwaysShowBatchFields?: boolean;
  /**
   * When the caller is a demand/plan document (Inbound / Order / ReturnOrder),
   * set this to `true`. ALL inventory tracking attribute Form.Item rules —
   * batch, manufacturing_date, expiration_date, custom_attributes — will be
   * forced to `required: false`, regardless of the product's
   * batch_management_required / manufacturing_date_required / validity_days
   * flags or each custom attribute definition's `required` flag.
   *
   * Default is `false` (acceptance mode — for Receipt / ReturnReceipt
   * verification forms, where required fields MUST be filled).
   *
   * Rationale: demand-side documents (plan) are pre-arrival; the operator
   * legitimately doesn't know the actual batch / mfg / expiry / attribute
   * values yet. Forcing required there blocks legitimate plan creation.
   * Required enforcement belongs at receipt/verify time (acceptance layer).
   *
   * See project_inventory_fact_vs_demand_layer memory for the design
   * rationale. The corresponding backend enforcement lives in
   * Inventories::RequiredAttributesValidator (called from receipt finalize,
   * return-receipt verify, and inventory-transformation approve actions).
   *
   * Also affects display: when true, manufacturing_date is always shown
   * (mirrors the previous mfgFieldOptional show-rule).
   */
  inventoryAttributesOptional?: boolean;
  hideQuantity?: boolean;
  // ── 三軌模式 ──
  inventoryMode?: InventoryMode;          // 'input' | 'precise'
  inventoryService?: InventoryService | null; // Precise mode: 查庫存的 service
  sourceWarehouseId?: string | null;          // Precise mode: admin/STO 需要傳 warehouse_id
  // ── 統一搜尋（組合商品支援）──
  bundleService?: unknown | null;             // 傳入時啟用 UnifiedItemSearch（同時搜商品＋組合）
  onSelectBundle?: ((bundle: Bundle, fieldIndex: number) => void) | null;
  searchMerchantId?: string | null;           // 搜尋用的 merchant_id
  // ── 整張卡片禁用（平台訂單等情境）──
  disabled?: boolean;
  // ── 未知商品標示（OrderEdit 載入既有 unknown_product item 時為 true）──
  isUnknownProduct?: boolean;
  unknownProductId?: string | null;
  /**
   * Multi-select mode for inventory picker (precise mode only).
   * When true, the modal shows checkboxes and confirms a batch; the parent
   * form is responsible for expanding picks into N items via onMultiSelectInventory.
   */
  multiSelectInventory?: boolean;
  onMultiSelectInventory?: (
    picks: Array<{ inventory: InventoryItem; allocatedQuantity: number }>,
  ) => void;
  /** Inventory IDs already used in sibling items — disabled in the modal. */
  excludeInventoryIds?: string[];
  /**
   * 已出貨數量（partial_shipped order 的 row 用）
   * 0 / undefined → 一般行為
   * > 0 → 所有非 quantity 欄位 disabled、quantity InputNumber min=shippedQuantity、
   *       remove 按鈕隱藏、quantity 欄位旁 render「已出 X/Y」badge
   */
  shippedQuantity?: number;
}

/**
 * Shared Product Item Card — 三軌模式統一元件
 *
 * Zone 1: 基本（商品、庫存類型、數量）— 永遠顯示
 * Zone 2: 庫存屬性（隨 inventoryMode 切換）
 *   - auto:    hidden
 *   - precise: 庫存批次下拉（label 已含完整資訊）
 *   - input:   batch / expiration_date / custom_attributes 手動輸入
 * Zone 3: 次要屬性（renderExtraFields）— 永遠顯示
 */
const ProductItemCard: React.FC<ProductItemCardProps> = ({
  restField,
  name,
  onRemove,
  searchProductsService,
  inventoryTypes,
  fetchInventoryTypes,
  attributeDefinitions = [],
  renderExtraFields,
  form,
  quantityLabel,
  quantityReadOnly = false,
  productReadOnly = false,
  listName,
  quantityFieldName = 'quantity',
  role = 'admin',
  alwaysShowBatchFields = false,
  inventoryAttributesOptional = false,
  hideQuantity = false,
  // ── 三軌模式 ──
  inventoryMode = 'input',      // 'input' | 'precise'
  inventoryService = null,      // Precise mode: 查庫存的 service
  sourceWarehouseId = null,     // Precise mode: admin/STO 需要傳 warehouse_id
  // ── 統一搜尋（組合商品支援）──
  bundleService = null,         // 傳入時啟用 UnifiedItemSearch（同時搜商品＋組合）
  onSelectBundle = null,        // (bundle, fieldIndex) => void
  searchMerchantId = null,      // 搜尋用的 merchant_id
  // ── 整張卡片禁用（平台訂單等情境）──
  disabled = false,
  // ── 未知商品標示（OrderEdit 載入既有 unknown_product item 時為 true）──
  isUnknownProduct = false,
  unknownProductId = null,
  multiSelectInventory = false,
  onMultiSelectInventory,
  excludeInventoryIds = [],
  // ── 已出貨數量（partial_shipped order 編輯）──
  shippedQuantity = 0,
}) => {
  const isShipped = (shippedQuantity ?? 0) > 0;
  // .jsx 子元件 / 寬泛 Select wrapper 邊界：本檔以 ComponentType<any> 包過。
  // Select wrapper 的 Props 在原檔註解裡明示「callers 仍以 any 包過」（rest 透傳 antd Select 不在型別內）；
  // SelectNotFoundContent 是 .jsx，JSDoc 推導出奇怪型別（destructured @param 寫法不符 TS 慣例），
  // 一律走 cast 而不去動子元件。
  const Select = SelectImpl as React.ComponentType<any>;
  const SelectNotFoundContent = SelectNotFoundContentImpl as React.ComponentType<any>;

  const { t } = useTranslation();
  const operatorData = useOperatorData();
  const merchantData = useMerchantData();
  const [reloading, setReloading] = React.useState<boolean>(false);
  const [localInventoryTypes, setLocalInventoryTypes] = React.useState<InventoryTypeRow[] | null>(null);

  // ── Precise mode state ──
  const initialItemValues = form.getFieldValue([listName, name]) || {};
  const sourceInv: InventoryItem | null = initialItemValues._source_inventory;

  const [selectedInventory, setSelectedInventory] = useState<InventoryItem | null>(() => sourceInv || null);
  const [inventoryModalVisible, setInventoryModalVisible] = useState<boolean>(false);
  // Captured at modal-open time so InventorySelectorModal (multi-select) can prefill
  // "目標數量" with this row's current quantity when the user clicks 變更/更換.
  const [modalInitialTargetQuantity, setModalInitialTargetQuantity] = useState<number | null>(null);

  const isPrecise = inventoryMode === 'precise';
  const isInput = inventoryMode === 'input';

  // Reactive watch on product_id so Precise mode button enables after product selection
  const watchedProductId = Form.useWatch([listName, name, 'product_id'], form);

  // Reactive watch on _source_inventory so external mutations (parent form's multi-select
  // expansion writes here via setFieldValue) sync into local selectedInventory state.
  // Without this, the UI keeps showing the "Select Inventory" button even after the parent
  // has filled the form value.
  const watchedSourceInventory = Form.useWatch([listName, name, '_source_inventory'], form);
  React.useEffect(() => {
    if (watchedSourceInventory) {
      setSelectedInventory(watchedSourceInventory as InventoryItem);
    }
  }, [watchedSourceInventory]);

  // Precise mode: 開啟庫存選擇 modal（需要先選商品）
  const handleOpenInventoryModal = useCallback(() => {
    const itemValues = form.getFieldValue([listName, name]) || {};
    const productId = itemValues.product_id?.value || itemValues.product_id;
    if (!productId) {
      return; // 沒選商品不開 modal
    }
    const currentQuantity = itemValues[quantityFieldName];
    setModalInitialTargetQuantity(typeof currentQuantity === 'number' ? currentQuantity : null);
    setInventoryModalVisible(true);
  }, [form, listName, name, quantityFieldName]);

  // ── 庫存類型相關 ──
  const handleReloadInventoryTypes = async () => {
    setReloading(true);
    try {
      if (role === 'operator' && operatorData?.refreshMerchantInventoryTypes) {
        const result = await operatorData.refreshMerchantInventoryTypes();
        const enabled: InventoryTypeRow[] = (result || [])
          .filter((sit: { merchant_id: string; enabled?: boolean; inventory_type: InventoryTypeRow }) =>
            sit.merchant_id === operatorData.merchant?.id && sit.enabled !== false)
          .map((sit: { inventory_type: InventoryTypeRow }) => sit.inventory_type);
        setLocalInventoryTypes(enabled);
      } else if (role === 'admin' && merchantData?.refreshInventoryTypes) {
        const result = await merchantData.refreshInventoryTypes();
        setLocalInventoryTypes(result || []);
      }
    } finally {
      setReloading(false);
    }
  };

  React.useEffect(() => {
    if (localInventoryTypes && inventoryTypes && inventoryTypes.length > 0) {
      setLocalInventoryTypes(null);
    }
  }, [inventoryTypes]);

  const effectiveTypes = localInventoryTypes || inventoryTypes;
  const { inventoryTypeOptions, inventoryTypeFetchData } = useMemo(() => {
    if (effectiveTypes && Array.isArray(effectiveTypes)) {
      return {
        inventoryTypeOptions: effectiveTypes.map((it: InventoryTypeRow) => ({
          label: it.code && it.code !== it.name ? `${it.code} - ${it.name}` : it.name,
          value: it.id
        })),
        inventoryTypeFetchData: null as FetchInventoryTypes
      };
    }
    return {
      inventoryTypeOptions: [] as Array<{ label: string; value: string }>,
      inventoryTypeFetchData: fetchInventoryTypes ?? null
    };
  }, [effectiveTypes, fetchInventoryTypes]);

  const inventoryTypeNotFoundContent = role === 'operator' ? (
    <SelectNotFoundContent
      hint={t('shared.product_item_card.no_inventory_types_hint_operator')}
      settingsPath="/operator/data/merchant_inventory_types"
      settingsLabel={t('shared.product_item_card.go_to_merchant_inventory_types')}
      onReload={handleReloadInventoryTypes}
      reloadLoading={reloading}
    />
  ) : (
    <SelectNotFoundContent
      hint={t('shared.product_item_card.no_inventory_types_hint_admin')}
      settingsPath="/admin/merchant_inventory_types"
      settingsLabel={t('shared.product_item_card.go_to_merchant_inventory_types')}
      onReload={merchantData?.refreshInventoryTypes ? handleReloadInventoryTypes : undefined}
      reloadLoading={reloading}
    />
  );

  // ── 商品搜尋 ──
  const handleFetchProducts = async (params: SelectFetchDataParams): Promise<SelectFetchDataResult> => {
    if (!searchProductsService) return { options: [] };
    try {
      // sourceWarehouseId 存在時帶 warehouse_id，後端會過濾成「該倉有庫存紀錄」的商品；
      // 移倉單建立／編輯尤其需要，避免使用者選到該倉根本沒庫存的品項
      const queryParams: Record<string, unknown> = {
        search: params.search,
        limit: params.pageSize,
        since_id: params.sinceId,
      };
      if (sourceWarehouseId) queryParams.warehouse_id = sourceWarehouseId;
      const result: any = await searchProductsService.fetchList(queryParams);
      const products: ProductLike[] = Array.isArray(result) ? result : (result?.data || []);
      return {
        options: products.map(mapProductToOption),
        hasMore: result?.has_more || false,
        sinceId: result?.since_id || null
      };
    } catch (error) {
      console.error("ProductItemCard searchProductsService error:", error);
      return { options: [] };
    }
  };

  // ── 商品選擇 ──
  const handleProductSelect = useCallback(async (option: ProductOption | null | undefined) => {
    const currentValues = form.getFieldValue([listName, name]) || {};

    if (!option || !option.value) {
      form.setFieldValue([listName, name], {
        ...currentValues,
        product_id: null,
        product_sku: '',
        product_name: '',
        product_validity_days: 0,
        manufacturing_date_required: false,
        batch_management_required: false,
        expiration_date: null,
        batch: '',
        inventory_id: null,
      });
      setSelectedInventory(null);
      return;
    }

    form.setFieldValue([listName, name], {
      ...currentValues,
      product_id: option.value,
      product_sku: option.sku || '',
      product_name: option.name || option.label || '',
      product_validity_days: option.validity_days || 0,
      manufacturing_date_required: !!option.manufacturing_date_required,
      batch_management_required: !!option.batch_management_required,
      inventory_id: null,
    });
    setSelectedInventory(null);
  }, [form, listName, name]);

  // ── 統一搜尋 handlers ──
  const handleUnifiedProductSelect = useCallback((product: ProductLike) => {
    handleProductSelect({
      value: product.id,
      label: `${product.sku} - ${product.name}`,
      sku: product.sku,
      name: product.name,
      validity_days: product.validity_days,
      manufacturing_date_required: product.manufacturing_date_required,
      batch_management_required: product.batch_management_required,
    });
  }, [handleProductSelect]);

  const handleUnifiedBundleSelect = useCallback((bundle: Bundle) => {
    if (onSelectBundle) onSelectBundle(bundle, name);
  }, [onSelectBundle, name]);

  // ── Precise mode: 從 modal 選擇庫存 ──
  const handleInventorySelect = useCallback((inv: InventoryItem) => {
    setSelectedInventory(inv);
    setInventoryModalVisible(false);

    const currentValues = form.getFieldValue([listName, name]) || {};
    form.setFieldValue([listName, name], {
      ...currentValues,
      inventory_id: inv.id,
      inventory_type_id: inv.inventory_type ? { value: inv.inventory_type.id, label: inv.inventory_type.name } : currentValues.inventory_type_id,
      batch: inv.batch || null,
      // BE returns ISO-string dates; antd DatePicker requires Dayjs. See toDayjsOrNull.
      expiration_date: toDayjsOrNull(inv.expiration_date),
      manufacturing_date: toDayjsOrNull(inv.manufacturing_date),
      custom_attributes: inv.custom_attributes || {},
    });
  }, [form, listName, name]);

  // ── Custom attribute input renderer ──
  const renderAttributeInput = (def: AttributeDefinition) => {
    const commonProps = {
      style: { width: '100%' },
      size: 'small' as const,
      disabled: disabled || isShipped
    };

    if (def.data_type === 'select') {
      // 用 antd 原生 Select 而不是 ui/Select wrapper —— wrapper 是給商品搜尋這類
      // 需要 async fetch / labelInValue 的情境用的，靜態選項用它會跟 Input/DatePicker
      // 視覺不對齊，且還要在 getValueFromEvent 裡解 labelInValue 物件。
      return <AntdSelect {...commonProps} placeholder={t('common.placeholder.select')} allowClear={!def.required} options={(def.options as string[] | undefined)?.map((opt: string) => ({ label: opt, value: opt }))} />;
    } else if (def.data_type === 'number') {
      return <InputNumber {...commonProps} />;
    } else if (def.data_type === 'date') {
      return <DatePicker {...commonProps} format="YYYY-MM-DD" />;
    } else if (def.data_type === 'boolean') {
      // Boolean is always binary; Schema 層 (ProductAttributeDefinition)
      // 已禁止 boolean + required=true，因此這裡永遠用 Switch。
      return <Switch disabled={disabled || isShipped} />;
    } else {
      return <Input {...commonProps} />;
    }
  };

  return (
    <Card
      className="product-item-card"
      style={{ marginBottom: 16 }}
      styles={{ body: { padding: '16px' } }}
    >
      <Row gutter={16} align="top">
        <Col span={22}>
          <Row gutter={16}>
            {/* ══════ Zone 1: 基本（商品、庫存類型、數量）══════ */}
            <Col span={isPrecise ? 10 : (hideQuantity ? 14 : 12)}>
              <Form.Item
                {...restField}
                name={[name, 'product_id']}
                label={
                  isUnknownProduct ? (
                    <Space size={8}>
                      <span>{t('shared.inbounds.form.product')}</span>
                      <Tag color="warning" style={{ margin: 0 }}>
                        {t('models.unknown_product._')}
                      </Tag>
                    </Space>
                  ) : (
                    t('shared.inbounds.form.product')
                  )
                }
                rules={[{ required: true, message: t('common.validation.required') }]}
              >
                {!productReadOnly && watchedProductId ? (
                  // Product already chosen → show as text display with a clear (×) button.
                  // Reasons:
                  //   1. Better UX — once picked, you don't need a fully searchable Select.
                  //   2. Robust against fresh ProductItemCard instances whose Select wrapper
                  //      has an empty optionMap (e.g. multi-select expansion appends new
                  //      cards with labelInValue values that the Select can't resolve to a
                  //      label without re-fetching).
                  //   3. Matches the existing bundleService-mode pattern.
                  <FormFieldDisplay>
                    <Tooltip title={isShipped ? t('pages.orders.edit.row_locked_tooltip') : undefined}>
                      <div style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        padding: '4px 11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minHeight: 32,
                        background: '#fff',
                      }}>
                        <span>
                          <Text strong>{form.getFieldValue([listName, name, 'product_sku'])}</Text>
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            {form.getFieldValue([listName, name, 'product_name'])}
                          </Text>
                        </span>
                        {!isShipped && (
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseCircleOutlined />}
                            onClick={() => handleProductSelect(null)}
                            disabled={disabled}
                          />
                        )}
                      </div>
                    </Tooltip>
                  </FormFieldDisplay>
                ) : bundleService && !productReadOnly ? (
                  <FormFieldDisplay>
                    <UnifiedItemSearch
                      productService={{ fetchList: (params: FetchListParams) => searchProductsService!.fetchList(params as unknown as Record<string, unknown>) }}
                      bundleService={bundleService}
                      merchantId={searchMerchantId}
                      onSelectProduct={handleUnifiedProductSelect}
                      onSelectBundle={handleUnifiedBundleSelect}
                      block
                      buttonType="dashed"
                      buttonText={t('shared.orders.form.unified_search.placeholder')}
                      disabled={disabled || isShipped}
                    />
                  </FormFieldDisplay>
                ) : (
                  <Select
                    data-testid={`${listName}-${name}-product-select`}
                    placeholder={t('common.placeholder.search_product')}
                    fetchData={handleFetchProducts}
                    pageSize={20}
                    isSearchable
                    isClearable={!productReadOnly && !disabled && !isShipped}
                    isDisabled={productReadOnly || disabled || isShipped}
                    onChange={handleProductSelect}
                    style={{ width: '100%' }}
                  />
                )}
              </Form.Item>
              {/* Hidden fields */}
              <Form.Item name={[name, 'product_sku']} noStyle hidden><Input /></Form.Item>
              <Form.Item name={[name, 'product_name']} noStyle hidden><Input /></Form.Item>
              <Form.Item name={[name, 'product_validity_days']} noStyle hidden><Input /></Form.Item>
              <Form.Item name={[name, 'manufacturing_date_required']} noStyle hidden><Input /></Form.Item>
              <Form.Item name={[name, 'batch_management_required']} noStyle hidden><Input /></Form.Item>
              <Form.Item name={[name, 'bundle_id']} noStyle hidden><Input /></Form.Item>
              <Form.Item name={[name, 'bundle_key']} noStyle hidden><Input /></Form.Item>
              <Form.Item name={[name, 'bundle_per_set_qty']} noStyle hidden><Input /></Form.Item>
              <Form.Item name={[name, 'id']} noStyle hidden><Input /></Form.Item>
              <Form.Item name={[name, 'inventory_id']} noStyle hidden><Input /></Form.Item>

              {isUnknownProduct && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: -12, marginBottom: 12 }}
                  message={t('models.unknown_product.unknown_product_warning')}
                  action={
                    unknownProductId ? (
                      <Link to={`/unknown_products/${unknownProductId}`}>
                        <Button size="small" type="link">
                          {t('models.unknown_product.map_this_product')}
                        </Button>
                      </Link>
                    ) : null
                  }
                />
              )}
            </Col>

            {/* Precise mode: 按鈕開 modal 選庫存批次 */}
            {isPrecise ? (
              <Col span={10}>
                <Form.Item
                  label={t('shared.orders.form.item_mode.select_inventory', { defaultValue: '選擇庫存批次' })}
                >
                  {selectedInventory ? (
                    <div
                      onClick={(disabled || isShipped) ? undefined : handleOpenInventoryModal}
                      style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        padding: '8px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'border-color 0.2s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1677ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d9d9d9'; }}
                    >
                      <InventoryBatchTags
                        inventoryTypeName={selectedInventory.inventory_type?.name}
                        batch={selectedInventory.batch}
                        expirationDate={selectedInventory.expiration_date}
                        manufacturingDate={selectedInventory.manufacturing_date}
                        customAttributes={selectedInventory.custom_attributes}
                        availableQuantity={selectedInventory.available_quantity ?? 0}
                      />
                      <Button type="link" size="small" icon={<SearchOutlined />} style={{ padding: 0 }}>
                        {t('common.button.change', { defaultValue: '變更' })}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      icon={<SearchOutlined />}
                      onClick={handleOpenInventoryModal}
                      disabled={disabled || isShipped || !watchedProductId}
                      style={{ width: '100%' }}
                      danger={!watchedProductId ? false : true}
                      type={!watchedProductId ? 'default' : 'dashed'}
                    >
                      {t('shared.orders.form.item_mode.select_inventory', { defaultValue: '選擇庫存批次' })}
                    </Button>
                  )}
                </Form.Item>
                <Form.Item name={[name, 'inventory_id']} noStyle hidden><Input /></Form.Item>
              </Col>
            ) : (
              <Col span={hideQuantity ? 10 : 6}>
                <Tooltip title={isShipped ? t('pages.orders.edit.row_locked_tooltip') : undefined}>
                  <div>
                    <Form.Item
                      {...restField}
                      name={[name, 'inventory_type_id']}
                      label={t('models.inventory_type._')}
                      rules={[{ required: true, message: t('common.validation.required') }]}
                    >
                      <Select
                        placeholder={t('common.placeholder.select')}
                        options={inventoryTypeOptions}
                        fetchData={inventoryTypeFetchData}
                        pageSize={20}
                        isSearchable
                        filterOption={(option: { label: string }, input: string) => option.label.toLowerCase().includes(input.toLowerCase())}
                        style={{ width: '100%' }}
                        notFoundContent={inventoryTypeNotFoundContent}
                        isDisabled={disabled || isShipped}
                      />
                    </Form.Item>
                  </div>
                </Tooltip>
              </Col>
            )}

            {!hideQuantity && (
              <Col span={isPrecise ? 4 : 6}>
                <Form.Item
                  {...restField}
                  name={[name, quantityFieldName]}
                  label={quantityLabel || t('models.inventory.quantity')}
                  rules={[{ required: true, message: t('common.validation.required') }]}
                  extra={isShipped ? t('pages.orders.edit.quantity_min_hint', { n: shippedQuantity }) : undefined}
                >
                  {isShipped ? (
                    <InputNumber
                      min={shippedQuantity}
                      style={{ width: '100%' }}
                      disabled={disabled || quantityReadOnly}
                      addonAfter={
                        <Tag color="blue" style={{ margin: 0 }}>
                          {t('pages.orders.edit.row_shipped_badge', {
                            shipped: shippedQuantity,
                            total: form.getFieldValue([listName, name, quantityFieldName]),
                          })}
                        </Tag>
                      }
                    />
                  ) : (
                    <InputNumber min={1} style={{ width: '100%' }} disabled={disabled || quantityReadOnly} />
                  )}
                </Form.Item>
              </Col>
            )}

            {/* ══════ Zone 2: 庫存屬性（隨模式切換）══════ */}

            {/* Input mode: batch / exp / mfg_date 手動輸入 */}
            {isInput && (
              <Col span={24}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => {
                    const prevItem = prevValues[listName]?.[name];
                    const currItem = currentValues[listName]?.[name];
                    return (
                      prevItem?.product_validity_days !== currItem?.product_validity_days ||
                      prevItem?.manufacturing_date_required !== currItem?.manufacturing_date_required ||
                      prevItem?.batch_management_required !== currItem?.batch_management_required
                    );
                  }}
                >
                  {({ getFieldValue }) => {
                    const item = getFieldValue([listName, name]) || {};
                    const hasValidPeriod = item.product_validity_days > 0;
                    const mfgRequired = !!item.manufacturing_date_required;
                    const batchProductRequired = !!item.batch_management_required;

                    // Demand-side forms (Inbound / Order / ReturnOrder) pass
                    // inventoryAttributesOptional=true so all tracking-attribute
                    // rules collapse to required:false regardless of the
                    // product's flags. Acceptance forms (Receipt /
                    // ReturnReceipt) keep the product flag as source of truth.
                    const attributesOptional = !!inventoryAttributesOptional;

                    const showMfgDate = attributesOptional || alwaysShowBatchFields || mfgRequired || hasValidPeriod;
                    const showExpDate = alwaysShowBatchFields || hasValidPeriod;
                    const mfgRuleRequired = mfgRequired && !attributesOptional;
                    const batchRuleRequired = batchProductRequired && !attributesOptional;
                    // expiration_date currently has no product-level "required"
                    // flag, so the effective required-rule is always false.
                    // Kept as a named const so a future expiry-required flag
                    // plugs in the same way as mfg/batch (replace `false`
                    // below with the new flag): `flag && !attributesOptional`.
                    const expirationRuleRequired = false;

                    return (
                      <Row gutter={16}>
                        {showMfgDate && (
                          <Col span={6}>
                            <Form.Item {...restField} name={[name, 'manufacturing_date']} label={t('models.inventory.manufacturing_date')} rules={[{ required: mfgRuleRequired, message: t('common.validation.required') }]}>
                              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder={t('common.placeholder.select_date')} disabled={disabled || isShipped} />
                            </Form.Item>
                          </Col>
                        )}
                        {showExpDate && (
                          <Col span={6}>
                            <Form.Item {...restField} name={[name, 'expiration_date']} label={t('models.inventory.expiration_date')} rules={[{ required: expirationRuleRequired, message: t('common.validation.required') }]}>
                              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder={t('common.placeholder.select_date')} disabled={disabled || isShipped} />
                            </Form.Item>
                          </Col>
                        )}
                        <Col span={6}>
                          <Form.Item {...restField} label={t('models.inventory.batch')} name={[name, 'batch']} rules={[{ required: batchRuleRequired, message: t('common.validation.required') }]}>
                            <Input placeholder={t('common.placeholder.enter_batch')} disabled={disabled || isShipped} />
                          </Form.Item>
                        </Col>
                      </Row>
                    );
                  }}
                </Form.Item>
              </Col>
            )}

            {/* Input mode: custom attributes */}
            {isInput && attributeDefinitions && attributeDefinitions.length > 0 && (
              <Col span={24}>
                <Row gutter={16} style={{ marginTop: 8 }}>
                  {attributeDefinitions.map(def => {
                    // Schema 層 (ProductAttributeDefinition) 已禁 boolean+required=true，
                    // boolean 永遠用 Switch（`checked` + implicit false default）。
                    const isBoolean = def.data_type === 'boolean';
                    return (
                    <Col span={8} key={def.id}>
                      <Form.Item
                        {...restField}
                        name={[name, 'custom_attributes', def.name]}
                        label={def.label}
                        rules={[{ required: !!def.required && !inventoryAttributesOptional, message: t('common.validation.required') }]}
                        valuePropName={isBoolean ? 'checked' : 'value'}
                        initialValue={isBoolean ? false : undefined}
                        getValueProps={(value) => {
                          switch (def.data_type) {
                            case 'date':    return { value: value ? dayjs(value as string) : null };
                            case 'boolean': return { checked: !!value };
                            default:        return { value };
                          }
                        }}
                        getValueFromEvent={(event) => {
                          switch (def.data_type) {
                            case 'date':    return event ? (event as Dayjs).format('YYYY-MM-DD') : null;
                            case 'boolean': return !!event;   // Switch onChange → boolean
                            case 'number':  return event;       // InputNumber → raw number | null
                            case 'select':  return event;       // antd Select onChange → option's value directly
                            default:        return (event as React.ChangeEvent<HTMLInputElement>)?.target?.value ?? '';  // Input → DOM event
                          }
                        }}
                      >
                        {renderAttributeInput(def)}
                      </Form.Item>
                    </Col>
                    );
                  })}
                </Row>
              </Col>
            )}

            {/* ══════ Zone 3: 次要屬性（永遠顯示）══════ */}
            {renderExtraFields && (
              <Col span={24}>
                {renderExtraFields(name, restField, { disabled: disabled || isShipped })}
              </Col>
            )}
          </Row>
        </Col>

        {/* Delete Button */}
        <Col span={2} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {!isShipped && (
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onRemove(name)} disabled={disabled}>
              {t('common.button.delete')}
            </Button>
          )}
        </Col>
      </Row>

      {/* Precise mode: 庫存選擇 Modal */}
      {isPrecise && (
        <InventorySelectorModal
          visible={inventoryModalVisible}
          onCancel={() => setInventoryModalVisible(false)}
          onSelect={handleInventorySelect}
          productId={(() => {
            const v = form.getFieldValue([listName, name, 'product_id']);
            return v?.value || v;
          })()}
          productName={form.getFieldValue([listName, name, 'product_name'])}
          merchantId={searchMerchantId}
          warehouseId={sourceWarehouseId}
          inventoryService={inventoryService}
          attributeDefinitions={attributeDefinitions}
          excludeIds={excludeInventoryIds}
          multiSelect={multiSelectInventory}
          initialTargetQuantity={modalInitialTargetQuantity}
          onConfirmMultiple={(picks: Array<{ inventory: InventoryItem; allocatedQuantity: number }>) => {
            setInventoryModalVisible(false);
            onMultiSelectInventory?.(picks);
          }}
        />
      )}
    </Card>
  );
};

export default ProductItemCard;
